/**
 * Media Buyer Logo Library for Impulsion Diagrams
 *
 * Provides pre-built library items with logos for common media buyer tools.
 * Each logo is an SVG rendered as a data URL inside an Excalidraw image element.
 */

import type { LibraryItem } from "@excalidraw/excalidraw/types";
import type { FileId } from "@excalidraw/element/types";

// Simple SVG logo generators - returns data URL
const svgToDataURL = (svg: string): string =>
  `data:image/svg+xml;base64,${btoa(svg)}`;

// Logo SVGs - simplified flat icons
const LOGOS: Record<string, { svg: string; color: string; label: string }> = {
  "google-ads": {
    label: "Google Ads",
    color: "#4285F4",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(15,15) scale(0.7)">
        <path d="M33.3 73.3L13.3 40 33.3 6.7h40l-20 33.3 20 33.3z" fill="#FBBC04"/>
        <circle cx="73.3" cy="73.3" r="13.3" fill="#4285F4"/>
        <path d="M33.3 73.3L13.3 40l20-33.3" stroke="#34A853" stroke-width="0" fill="#34A853"/>
        <rect x="10" y="53" width="27" height="27" rx="13.5" fill="#34A853"/>
        <path d="M13.3 40L33.3 6.7h40L53.3 40z" fill="#FBBC04"/>
        <circle cx="73.3" cy="73.3" r="13.3" fill="#4285F4"/>
        <rect x="10" y="53" width="27" height="27" rx="13.5" fill="#34A853"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#333">Google Ads</text>
    </svg>`,
  },
  "meta-ads": {
    label: "Meta Ads",
    color: "#0081FB",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(20,18)">
        <path d="M0 32c0-8 2.5-14.5 6.5-18.5C10 10 14.5 8.5 18.5 8.5c5 0 9.5 3 14 9l3 4 3-4c4.5-6 9-9 14-9 4 0 8.5 1.5 12 5 4 3.5 6.5 10.5 6.5 18.5 0 8-4 16.5-10 23.5-5.5 6.5-12.5 12-18 15.5L38 35l-5.5-8c-3-4.5-6-7-9-7-2.5 0-5 1.5-7 4.5-1.8 2.7-2.8 6.5-2.8 10.5 0 6 3 13 8.5 19s12 10.5 16.8 12.5c-3 1.8-6.5 3.5-9.5 4.5C24 69 18 63 13 56.5 7 48.5 0 39 0 32z" fill="#0081FB"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#333">Meta Ads</text>
    </svg>`,
  },
  ga4: {
    label: "GA4",
    color: "#E37400",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(22,15)">
        <rect x="0" y="36" width="14" height="28" rx="7" fill="#E37400"/>
        <rect x="22" y="18" width="14" height="46" rx="7" fill="#E37400"/>
        <circle cx="51" cy="16" r="8" fill="#E37400"/>
        <rect x="44" y="30" width="14" height="34" rx="7" fill="#E37400"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#333">Google Analytics</text>
    </svg>`,
  },
  gtm: {
    label: "GTM",
    color: "#4285F4",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(20,15)">
        <path d="M34 5L5 34l13 13L47 18z" fill="#8AB4F8"/>
        <path d="M34 5l29 29-13 13-29-29z" fill="#4285F4"/>
        <path d="M18 47l-13 13 29 29 13-13z" fill="#8AB4F8"/>
        <path d="M47 18L18 47l16 16 29-29z" fill="#246FDB"/>
        <circle cx="34" cy="60" r="8" fill="#FFFFFF"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#333">GTM</text>
    </svg>`,
  },
  tiktok: {
    label: "TikTok Ads",
    color: "#000000",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(28,12)">
        <path d="M33 0h8v36c0 12-9.5 22-22 22S0 48 0 36s9-22 19-22v9c-7 0-11 5.5-11 13s5 13 11 13 12-5.5 12-13V0z" fill="#000000"/>
        <path d="M33 0c0 9 5 16 13 18v9c-8 0-15.5-4-20-10" fill="#FE2C55" opacity="0.7"/>
        <path d="M7 36c0-6.5 4.5-12 10-13v-9c-10 1-18.5 10-18.5 22 0 12 9.5 22 22 22 3 0 5.5-.5 8-1.5-9-3-15.5-12-15.5-22.5" fill="#25F4EE" opacity="0.7"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#333">TikTok Ads</text>
    </svg>`,
  },
  snapchat: {
    label: "Snapchat Ads",
    color: "#FFFC00",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(20,15)">
        <path d="M30 0c12 0 20 8 22 22 0 3-0.5 6-1 9 3 1 6 2.5 6 5s-4 4-8 5c-1 6-6 14-19 15-5 0-8-3-13-3s-7 3-13 3C-9 55-14 47-15 41c-4-1-8-2.5-8-5s3-4 6-5c-0.5-3-1-6-1-9C-16 8-8 0 4 0s14 0 26 0z" fill="#FFFC00" stroke="#333" stroke-width="1.5" transform="translate(30,2) scale(0.55)"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#333">Snapchat Ads</text>
    </svg>`,
  },
  linkedin: {
    label: "LinkedIn Ads",
    color: "#0A66C2",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(22,15)">
        <rect width="56" height="56" rx="8" fill="#0A66C2"/>
        <rect x="10" y="24" width="10" height="22" fill="#FFFFFF"/>
        <circle cx="15" cy="16" r="6" fill="#FFFFFF"/>
        <path d="M26 24h9v3.5c1.5-2.5 4-4.5 8-4.5 8 0 10 5 10 12v11h-9V37c0-3-1-5.5-4.5-5.5s-4.5 2.5-4.5 5.5v9h-9V24z" fill="#FFFFFF"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#333">LinkedIn Ads</text>
    </svg>`,
  },
  pinterest: {
    label: "Pinterest Ads",
    color: "#E60023",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(25,12)">
        <circle cx="25" cy="25" r="25" fill="#E60023"/>
        <path d="M25 10c-10 0-16 7-16 15 0 4 2.5 9 6 10.5.5.2 1-.2 1-1v-2c-4-1.5-5-5.5-5-7.5 0-7 5-13 13-13s11 5 11 11c0 5.5-3 10-7 10-2 0-3.5-1.5-3-3.5 0.5-2.5 2-5 2-6.5 0-1.5-1-3-3-3-2.5 0-4.5 2.5-4.5 6 0 2 .5 3.5.5 3.5l-2 9c-0.5 3 0 7 0 7h1.5c1-3 2.5-7 3-9 0.5-1 2-4 2-4 1 1.5 3.5 3 6.5 3 8 0 14-7.5 14-17C44 17 36 10 25 10z" fill="#FFFFFF"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#333">Pinterest Ads</text>
    </svg>`,
  },
  "google-search-console": {
    label: "Search Console",
    color: "#4285F4",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(22,15)">
        <circle cx="24" cy="24" r="18" stroke="#4285F4" stroke-width="6" fill="none"/>
        <line x1="37" y1="37" x2="52" y2="52" stroke="#4285F4" stroke-width="6" stroke-linecap="round"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="8" fill="#333">Search Console</text>
    </svg>`,
  },
  hotjar: {
    label: "Hotjar",
    color: "#FF3C00",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(30,12)">
        <path d="M20 0c2 8-8 16-8 28s12 12 12 24-10 14-10 14" stroke="#FF3C00" stroke-width="8" stroke-linecap="round" fill="none"/>
        <path d="M35 0c2 8-8 16-8 28s12 12 12 24-10 14-10 14" stroke="#FF3C00" stroke-width="8" stroke-linecap="round" fill="none" opacity="0.5"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#333">Hotjar</text>
    </svg>`,
  },
  hubspot: {
    label: "HubSpot",
    color: "#FF7A59",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(25,15)">
        <circle cx="25" cy="30" r="18" stroke="#FF7A59" stroke-width="5" fill="none"/>
        <circle cx="25" cy="30" r="6" fill="#FF7A59"/>
        <line x1="25" y1="12" x2="25" y2="2" stroke="#FF7A59" stroke-width="5" stroke-linecap="round"/>
        <circle cx="44" cy="46" r="4" fill="#FF7A59"/>
        <line x1="38" y1="42" x2="42" y2="45" stroke="#FF7A59" stroke-width="3"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#333">HubSpot</text>
    </svg>`,
  },
  semrush: {
    label: "Semrush",
    color: "#FF642D",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(20,15)">
        <rect x="0" y="40" width="12" height="24" rx="3" fill="#FF642D"/>
        <rect x="16" y="28" width="12" height="36" rx="3" fill="#FF642D"/>
        <rect x="32" y="16" width="12" height="48" rx="3" fill="#FF642D"/>
        <rect x="48" y="4" width="12" height="60" rx="3" fill="#FF642D"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#333">Semrush</text>
    </svg>`,
  },
  shopify: {
    label: "Shopify",
    color: "#96BF48",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(28,10)">
        <path d="M38 12c0 0-1-.5-3-.5-1.5 0-3.5.5-5 2-1.5 1-2.5 3-3 5h-2l-1 3h3c-1 5-3 12-5 18-3 7-5 10-7 12-1.5 1-2 1-2.5 1s-.5-.5-.5-1c0-1 1-4 2-8l4-15h-3l-1 3C9 35 6 42 4 46c-1 3-2 5-2 7 0 2.5 1.5 4 3.5 4 2 0 4-1.5 6-4 2-2.5 4-6 6-12l3-11h3c1.5 0 2.5.5 2.5.5l1-3h0z" fill="#96BF48"/>
        <path d="M32 25c-1 0-2.5 1-4 3-1.5 2-3 5.5-3 9 0 3 1 5 3.5 5 1.5 0 3-1 4-3 1.5-2 3-5.5 3-9 0-3-1.5-5-3.5-5z" fill="#96BF48"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#333">Shopify</text>
    </svg>`,
  },
  klaviyo: {
    label: "Klaviyo",
    color: "#2D2D2D",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(22,18)">
        <polygon points="28,5 56,28 42,50 28,38 14,50 0,28" fill="#2D2D2D"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#333">Klaviyo</text>
    </svg>`,
  },
  pixel: {
    label: "Pixel (tracking)",
    color: "#4267B2",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(22,15)">
        <rect x="0" y="0" width="24" height="24" rx="4" fill="#4267B2"/>
        <rect x="32" y="0" width="24" height="24" rx="4" fill="#4267B2" opacity="0.6"/>
        <rect x="0" y="32" width="24" height="24" rx="4" fill="#4267B2" opacity="0.6"/>
        <rect x="32" y="32" width="24" height="24" rx="4" fill="#4267B2" opacity="0.3"/>
        <circle cx="12" cy="12" r="4" fill="#FFFFFF"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="9" fill="#333">Pixel</text>
    </svg>`,
  },
  "conversion-api": {
    label: "Conversion API",
    color: "#0081FB",
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
      <rect width="100" height="100" rx="16" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="2"/>
      <g transform="translate(18,15)">
        <rect x="0" y="8" width="64" height="48" rx="6" fill="none" stroke="#0081FB" stroke-width="3"/>
        <line x1="0" y1="20" x2="64" y2="20" stroke="#0081FB" stroke-width="2"/>
        <text x="32" y="42" text-anchor="middle" font-family="monospace" font-size="12" fill="#0081FB" font-weight="bold">&lt;/&gt;</text>
        <path d="M20 0v8M44 0v8" stroke="#0081FB" stroke-width="3" stroke-linecap="round"/>
      </g>
      <text x="50" y="92" text-anchor="middle" font-family="Arial,sans-serif" font-size="8" fill="#333">Conversion API</text>
    </svg>`,
  },
};

// Generate a deterministic ID from a string
const generateId = (prefix: string): string => {
  let hash = 0;
  for (let i = 0; i < prefix.length; i++) {
    const char = prefix.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return `impulsion_${prefix}_${Math.abs(hash).toString(36)}`;
};

// Create a library item from a logo
const createLibraryItem = (
  key: string,
  logo: { svg: string; label: string },
): { item: LibraryItem; fileId: string; dataURL: string } => {
  const fileId = generateId(`file_${key}`);
  const elementId = generateId(`img_${key}`);
  const textId = generateId(`txt_${key}`);

  const dataURL = svgToDataURL(logo.svg);

  const imageElement = {
    id: elementId,
    type: "image" as const,
    x: 0,
    y: 0,
    width: 80,
    height: 80,
    angle: 0,
    strokeColor: "transparent",
    backgroundColor: "transparent",
    fillStyle: "solid" as const,
    strokeWidth: 0,
    strokeStyle: "solid" as const,
    roughness: 0,
    opacity: 100,
    groupIds: [] as string[],
    frameId: null,
    index: "a0" as any,
    roundness: null,
    seed: Math.floor(Math.random() * 2000000000),
    version: 1,
    versionNonce: Math.floor(Math.random() * 2000000000),
    isDeleted: false,
    boundElements: null,
    updated: Date.now(),
    link: null,
    locked: false,
    fileId: fileId as unknown as FileId,
    status: "saved" as const,
    scale: [1, 1] as [number, number],
    crop: null,
  };

  const item: LibraryItem = {
    id: generateId(`lib_${key}`),
    status: "published",
    elements: [imageElement] as any,
    created: Date.now(),
    name: logo.label,
  };

  return { item, fileId, dataURL };
};

// Build all library items and file data
export const buildMediaBuyerLibrary = (): {
  libraryItems: LibraryItem[];
  files: Array<{ id: string; dataURL: string; mimeType: string }>;
} => {
  const libraryItems: LibraryItem[] = [];
  const files: Array<{ id: string; dataURL: string; mimeType: string }> = [];

  for (const [key, logo] of Object.entries(LOGOS)) {
    const { item, fileId, dataURL } = createLibraryItem(key, logo);
    libraryItems.push(item);
    files.push({ id: fileId, dataURL, mimeType: "image/svg+xml" });
  }

  return { libraryItems, files };
};
